import "reflect-metadata";
import {injectable} from "inversify";
import api, {parseServerError} from "@dropDesk/data/clients/http.client";
import {PlanExtensionCatalogEntity} from "@dropDesk/domain/entities/plan_extensions/plan_extension_catalog.entity";
import {PlanExtensionSimulateHireEntity} from "@dropDesk/domain/entities/plan_extensions/plan_extension_simulate_hire.entity";
import {CompanyExtensionEntity} from "@dropDesk/domain/entities/plan_extensions/company_extension.entity";

@injectable()
export abstract class PlanExtensionsCatalogRemoteDataSource {

    public abstract listCatalog(includeDeleted?: boolean): Promise<PlanExtensionCatalogEntity[]>;
    public abstract getCatalogById(id: string): Promise<PlanExtensionCatalogEntity>;
    public abstract simulateHire(idExtension: string): Promise<PlanExtensionSimulateHireEntity>;
    public abstract hire(idExtension: string): Promise<CompanyExtensionEntity>;
    public abstract unsubscribe(id: string): Promise<void>;
    public abstract reactivate(id: string): Promise<void>;
    public abstract listInstalled(): Promise<CompanyExtensionEntity[]>;
}

@injectable()
export class PlanExtensionsCatalogRemoteDataSourceImpl implements PlanExtensionsCatalogRemoteDataSource {

    tableName: string = 'plan-extensions';

    public listCatalog(includeDeleted?: boolean): Promise<PlanExtensionCatalogEntity[]> {
        return new Promise<PlanExtensionCatalogEntity[]>(async (resolve, reject) => {
            try {
                const params = includeDeleted ? `?includeDeleted=true` : '';
                const response = await api.get(`${this.tableName}/catalog${params}`);
                const result: PlanExtensionCatalogEntity[] = response.data.map(
                    (entry: Record<string, any>) => PlanExtensionCatalogEntity.fromJson(entry)
                );
                return resolve(result);
            } catch (error) {
                return reject(parseServerError(error));
            }
        });
    }

    public getCatalogById(id: string): Promise<PlanExtensionCatalogEntity> {
        return new Promise<PlanExtensionCatalogEntity>(async (resolve, reject) => {
            try {
                const response = await api.get(`${this.tableName}/catalog/${id}`);
                return resolve(PlanExtensionCatalogEntity.fromJson(response.data));
            } catch (error) {
                return reject(parseServerError(error));
            }
        });
    }

    public simulateHire(idExtension: string): Promise<PlanExtensionSimulateHireEntity> {
        return new Promise<PlanExtensionSimulateHireEntity>(async (resolve, reject) => {
            try {
                const response = await api.get(`${this.tableName}/simulate-hire`, {params: {idExtension}});
                return resolve(PlanExtensionSimulateHireEntity.fromJson(response.data));
            } catch (error) {
                return reject(parseServerError(error));
            }
        });
    }

    public hire(idExtension: string): Promise<CompanyExtensionEntity> {
        return new Promise<CompanyExtensionEntity>(async (resolve, reject) => {
            try {
                const response = await api.post(`${this.tableName}/hire`, {idExtension});
                return resolve(CompanyExtensionEntity.fromJson(response.data));
            } catch (error) {
                return reject(parseServerError(error));
            }
        });
    }

    public unsubscribe(id: string): Promise<void> {
        return new Promise<void>(async (resolve, reject) => {
            try {
                await api.delete(`${this.tableName}/unsubscribe/${id}`);
                return resolve();
            } catch (error) {
                return reject(parseServerError(error));
            }
        });
    }

    public reactivate(id: string): Promise<void> {
        return new Promise<void>(async (resolve, reject) => {
            try {
                await api.put(`${this.tableName}/reactivate/${id}`);
                return resolve();
            } catch (error) {
                return reject(parseServerError(error));
            }
        });
    }

    public listInstalled(): Promise<CompanyExtensionEntity[]> {
        return new Promise<CompanyExtensionEntity[]>(async (resolve, reject) => {
            try {
                const response = await api.get(`${this.tableName}/installed-extensions`);
                const result: CompanyExtensionEntity[] = response.data.map(
                    (entry: Record<string, any>) => CompanyExtensionEntity.fromJson(entry)
                );
                return resolve(result);
            } catch (error) {
                return reject(parseServerError(error));
            }
        });
    }
}
